import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoriesAPI } from '../../services/api';
import { Category, CategoryCreate } from '../../types';

/**
 * Query hooks for Categories.
 *
 * The query key root MUST be 'categories' — the same slug the backend's
 * realtime-invalidation middleware broadcasts for /api/categories. That's what
 * makes another user's change auto-refresh this list. Use this file as the
 * template when converting other entities (devis, factures, stock, …): copy it,
 * swap the API + key root to the entity's slug.
 */
export const categoriesKeys = {
  all: ['categories'] as const,
  deleted: ['categories', 'deleted'] as const,
};

export function useCategories() {
  return useQuery<Category[]>({
    queryKey: categoriesKeys.all,
    queryFn: categoriesAPI.getAll,
  });
}

export function useDeletedCategories(enabled: boolean) {
  return useQuery<Category[]>({
    queryKey: categoriesKeys.deleted,
    queryFn: categoriesAPI.getDeleted,
    enabled,
  });
}

/**
 * Invalidate every categories query (active + deleted) so the acting user sees
 * their own change instantly. Other users get the same refresh via WebSocket.
 */
function useInvalidateCategories() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: categoriesKeys.all });
}

export function useCreateCategory() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: (data: CategoryCreate) => categoriesAPI.create(data),
    onSuccess: invalidate,
  });
}

export function useUpdateCategory() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CategoryCreate> }) =>
      categoriesAPI.update(id, data),
    onSuccess: invalidate,
  });
}

export function useDeleteCategory() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: (category: Category) => categoriesAPI.delete(category.id),
    onSuccess: invalidate,
  });
}

export function useRestoreCategory() {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: (category: Category) => categoriesAPI.restore(category.id),
    onSuccess: invalidate,
  });
}
